import React, { useRef, useState } from 'react'
import { Button } from './Button'
import styles from './ImageSlot.module.scss'

export interface ImageSlotImage {
  url: string
  width?: number | null
  height?: number | null
}

interface ImageSlotProps {
  image: ImageSlotImage | null
  /** MIME types accepted by the file picker. Defaults to common raster formats. */
  accept?: string
  onUpload: (file: File) => Promise<void> | void
  onRemove?: () => Promise<void> | void
  disabled?: boolean
  /** Optional caption rendered above the slot. */
  label?: string
  /** Optional helper text rendered beneath the label. */
  hint?: string
  /**
   * Sync pre-upload validator. Return a string to surface as an error and
   * abort the upload; return null to accept.
   */
  validate?: (file: File) => string | null
  /** CSS aspect-ratio value, e.g. "16 / 9". Defaults to no enforcement. */
  aspectRatio?: string
  /** Tighter layout for dense tables — drops the min-height floor and the empty-state caption. */
  compact?: boolean
}

const DEFAULT_ACCEPT = 'image/jpeg,image/png,image/webp'

export const ImageSlot: React.FC<ImageSlotProps> = ({
  image,
  accept = DEFAULT_ACCEPT,
  onUpload,
  onRemove,
  disabled,
  label,
  hint,
  validate,
  aspectRatio,
  compact,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const openPicker = () => {
    if (disabled || busy) return
    inputRef.current?.click()
  }

  const handleFile = async (file: File) => {
    setError(null)
    if (validate) {
      const err = validate(file)
      if (err) { setError(err); return }
    }
    setBusy('upload')
    try {
      await onUpload(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setBusy(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleRemove = async () => {
    if (!onRemove || disabled || busy) return
    setError(null)
    setBusy('remove')
    try {
      await onRemove()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setBusy(null)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    if (disabled || busy) return
    const file = e.dataTransfer.files?.[0]
    if (file) void handleFile(file)
  }

  const body = image ? (
    <div
      className={`${styles.slot} ${styles.slotFilled} ${compact ? styles.slotCompact : ''} ${dragOver ? styles.slotDragOver : ''}`}
      style={aspectRatio ? { aspectRatio } : undefined}
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); if (!disabled && !busy) setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
    >
      <img src={image.url} alt="" className={styles.image} />
      {!disabled && (
        <div className={`${styles.hoverOverlay} ${compact ? styles.hoverOverlayCompact : ''}`}>
          {!compact && (
            <a
              href={image.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.openInNew}
              title="Open image in new tab"
              aria-label="Open image in new tab"
              onClick={(e) => e.stopPropagation()}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}
          <div className={styles.buttons}>
            <Button size="small" variant="secondary" onClick={openPicker} disabled={!!busy}>
              {busy === 'upload' ? 'Uploading…' : 'Replace'}
            </Button>
            {onRemove && (
              <Button size="small" variant="danger" onClick={handleRemove} disabled={!!busy}>
                {busy === 'remove' ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
        </div>
      )}
      {busy === 'upload' && <div className={styles.uploadingOverlay}>Uploading…</div>}
    </div>
  ) : (
    <div
      className={`${styles.slot} ${styles.slotEmpty} ${compact ? styles.slotCompact : ''} ${dragOver ? styles.slotDragOver : ''} ${disabled ? styles.slotDisabled : ''}`}
      style={aspectRatio ? { aspectRatio } : undefined}
      onClick={openPicker}
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); if (!disabled && !busy) setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled || busy) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker() }
      }}
      aria-label={label ? `Upload ${label}` : 'Upload image'}
    >
      <div className={styles.emptyIcon}>＋</div>
      {!compact && (
        <div className={styles.emptyText}>
          {busy === 'upload' ? 'Uploading…' : 'Drop image or click to upload'}
        </div>
      )}
    </div>
  )

  return (
    <div className={styles.wrapper}>
      {label && <div className={styles.label}>{label}</div>}
      {hint && <div className={styles.hint}>{hint}</div>}
      {body}
      {error && <div className={styles.error}>{error}</div>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void handleFile(f)
        }}
        className={styles.hiddenInput}
        disabled={disabled}
      />
    </div>
  )
}

export default ImageSlot
